import { errorHandler } from 'utilities/errorHandler'
import { api } from './index'
import type {
  OrderRating,
  OrderRatingDetail,
  OrderRatingDetailEnvelope,
  OrderRatingDetailResponse,
  OrderRatingNeighbors,
  OrderRatingNeighborsWire,
  OrderRatingQueryParams,
  OrderRatingRecentActivitiesParams,
  OrderRatingResponse,
  ResolveOrderRatingParams,
  SendOrderRatingMessageParams,
  UserOrderRatingsParams,
} from './orderRatings.types'
import type {
  AvailableReward,
  PaginatedResponse,
  PaginatedResponseWire,
  PaginationMeta,
  PaginationMetaWire,
} from './recoveryAi.types'

// Wire → view for one rating row. Every nullable field is preserved as `null`
// (never undefined / '') so the shared FeedbackTable normalizer downstream reads
// a uniform shape. Exported for a dedicated transform unit test — the slice's
// transformResponse delegates here (mirrors recoveryAi's transformRestrictedCustomer).
export const transformOrderRating = (
  wire: OrderRatingResponse
): OrderRating => ({
  id: wire.id,
  rating: wire.rating,
  date: wire.date,
  customerName: wire.customer_name,
  storeName: wire.store_name,
  resolvedBy: {
    kind: wire.resolved_by?.kind ?? null,
    name: wire.resolved_by?.name ?? null,
  },
  conversationId: wire.conversation_id,
  comment: wire.comment,
  // Rides through to the detail page in router state, where it keys the
  // customer + recent-ratings queries. Absent on a pre-field backend → null.
  userId: wire.user_id ?? null,
  userUid: wire.user_uid ?? null,
  // Recent-activities enrichments (AI-1025 / AI-1032). `returnedWithin30Days`
  // passes through as-is (undefined when the backend omits it) so the column
  // stays data-gated; `rewardName` coalesces to null (no gating — the
  // Resolution column always renders, falling back to the enum label).
  rewardName: wire.reward_name ?? null,
  returnedWithin30Days: wire.returned_within_30_days,
})

// nexus OrderRatings::BuildOrderSnapshot maps the thanx-ordering handoff through
// a DISPLAY table (HANDOFF_LABELS), so `order.delivery_method` arrives as a human
// label ('Pickup', 'Dine-in', 'Drive-thru', …), NOT the enum token the shared
// OrderSummaryStrip localizes against (KNOWN_DELIVERY_METHODS + the
// `delivery_methods` locale keys). Fold the six known labels back to their tokens
// here, at the wire boundary, so the strip's translateEnum finds a match and
// renders localized copy on both this surface and RAI. An unrecognized value
// rides through untouched — translateEnum still falls back to the raw string, so
// a new backend label degrades to readable text, not a missing-i18n key.
// Exported for its unit test.
const DELIVERY_METHOD_TOKENS: Readonly<Record<string, string>> = {
  Pickup: 'pickup',
  Delivery: 'delivery',
  Curbside: 'curbside',
  Tableside: 'tableside',
  'Drive-thru': 'drivethru',
  'Dine-in': 'dine_in',
}

export const normalizeDeliveryMethod = (
  wire: string | null | undefined
): string | null => {
  if (!wire) return null
  return DELIVERY_METHOD_TOKENS[wire] ?? wire
}

// Wire → view for the show endpoint: the row transform above plus the order
// snapshot, widened to the shared OrderSnapshot so the promoted
// components/OrderSummaryStrip (and its receipt modal) render this surface with
// no variant prop. promo + delivery_method now come from the wire (AI-1024);
// only customer first name is absent here and maps to null. `items` coalesces
// to [] — the strip gates its receipt affordance on a non-empty list. Exported
// for its unit test.
export const transformOrderRatingDetail = (
  wire: OrderRatingDetailResponse
): OrderRatingDetail => ({
  ...transformOrderRating(wire),
  order: wire.order
    ? {
        orderId: wire.order.order_id,
        orderNumber: wire.order.order_number,
        locationName: wire.order.location_name,
        orderDate: wire.order.order_date,
        orderTotalCents: wire.order.order_total_cents,
        subtotalCents: wire.order.subtotal_cents ?? null,
        taxCents: wire.order.tax_cents,
        promo: wire.order.promo ?? null,
        discounts: (wire.order.discounts ?? []).map(discount => ({
          label: discount.label,
          amountCents: discount.amount_cents,
        })),
        items: (wire.order.items ?? []).map(item => ({
          name: item.name,
          quantity: item.qty,
          priceCents: item.price_cents ?? undefined,
          modifier: item.modifier ?? null,
        })),
        deliveryMethod: normalizeDeliveryMethod(wire.order.delivery_method),
        customerFirstName: null,
      }
    : null,
})

// snake_case pagination meta → camelCase. Mirrors recoveryAi's module-private
// helper; kept local rather than importing it, because importing from that
// module would side-effect-inject the entire RAI slice. Hoisting both to a
// shared src/api pagination util is a sensible follow-up once a third slice
// needs it. Exported for its unit test.
export const normalizePaginationMeta = (
  wire: PaginationMetaWire
): PaginationMeta => ({
  currentPage: wire.current_page,
  totalPages: wire.total_pages,
  totalCount: wire.total_count,
  perPage: wire.per_page,
})

export const orderRatingsApi = api.injectEndpoints({
  endpoints: builder => ({
    getOrderRatings: builder.query<
      PaginatedResponse<OrderRating>,
      OrderRatingQueryParams
    >({
      query: params => ({
        url: 'order_ratings',
        params,
      }),
      transformResponse: (
        response: PaginatedResponseWire<OrderRatingResponse>
      ): PaginatedResponse<OrderRating> => ({
        meta: normalizePaginationMeta(response.meta),
        data: response.data.map(transformOrderRating),
      }),
      // Flat (unscoped) — the list is one cache entry per filter combination, and
      // any resolve invalidates every one of them.
      providesTags: ['OrderRating'],
    }),

    // The customer profile's Order feedback tab (AI-1288 / FR-30). See
    // UserOrderRatingsParams for why this is its own route rather than
    // getOrderRatings with a user_id filter.
    getUserOrderRatings: builder.query<
      PaginatedResponse<OrderRating>,
      UserOrderRatingsParams
    >({
      query: ({ identifier, ...params }) => ({
        url: `users/${identifier}/order_ratings`,
        params,
      }),
      transformResponse: (
        response: PaginatedResponseWire<OrderRatingResponse>
      ): PaginatedResponse<OrderRating> => ({
        meta: normalizePaginationMeta(response.meta),
        data: response.data.map(transformOrderRating),
      }),
      // Flat, mirroring getOrderRatings: a resolve anywhere (including from
      // this same tab) must refetch this list too.
      providesTags: ['OrderRating'],
    }),

    // The detail page's deep-link/refresh path. The row the operator clicked is
    // handed over in router state for an instant first paint; this query still
    // runs to enrich it with the order snapshot, and is the ONLY source when the
    // page is opened cold.
    getOrderRating: builder.query<OrderRatingDetail, string>({
      query: id => ({ url: `order_ratings/${id}` }),
      transformResponse: (
        response: OrderRatingDetailEnvelope
      ): OrderRatingDetail => transformOrderRatingDetail(response.order_rating),
      // Scoped to the record so resolving one rating refetches THAT detail page,
      // not every cached rating (the flat index tag below still catches the list).
      providesTags: (_result, _error, id) => [{ type: 'OrderRating', id }],
    }),

    // The header's "Case N of M unresolved" pager (AI-1023): this rating's
    // position within the unresolved set + its neighbor ids. `position` is null
    // when the rating isn't unresolved, which hides the pager. Scoped to the
    // record so a resolve refetches it (the set shifts when a rating closes).
    getOrderRatingNeighbors: builder.query<OrderRatingNeighbors, string>({
      query: id => ({ url: `order_ratings/${id}/neighbors` }),
      transformResponse: (
        response: OrderRatingNeighborsWire
      ): OrderRatingNeighbors => ({
        position: response.position,
        total: response.total,
        previousId: response.previous_id,
        nextId: response.next_id,
      }),
      // Both the scoped tag AND the flat one: resolving any rating shifts
      // position/total for the WHOLE unresolved set, not just the resolved
      // record, so a resolve's flat-tag invalidation (resolveOrderRating below)
      // must refetch every cached neighbors entry — otherwise a previously-viewed
      // rating keeps a stale "Case N of M" and prev/next pointers, possibly to a
      // now-resolved neighbor. Mirrors getOrderRatings' flat list tag.
      providesTags: (_result, _error, id) => [
        { type: 'OrderRating', id },
        'OrderRating',
      ],
      // The pager's ONLY telemetry. api/index's baseQuery special-cases just 403
      // and the legal-docs redirect, so a 404 here (the /neighbors route only
      // exists once nexus #1563 ships) is otherwise entirely silent: no toast, no
      // Sentry, and a null pager indistinguishable from a legitimately-resolved
      // rating (position null). Purely a side effect — the rejection is neither
      // swallowed nor re-thrown, so the hook's own isError is untouched. Mirrors
      // getGrantableRewards below.
      onQueryStarted: async (id, { queryFulfilled }) => {
        try {
          await queryFulfilled
        } catch (rejection) {
          errorHandler.capture(
            'order_ratings/:id/neighbors failed; the order-feedback "Case N of M" pager will render empty (indistinguishable from a resolved rating)',
            {
              endpoint: 'getOrderRatingNeighbors',
              // Only the pager envelope (positions + ids) rides on this endpoint —
              // no customer data.
              id,
              rejection,
              ticket: 'AI-1023',
              likely_cause:
                'nexus order_ratings/:id/neighbors not yet deployed',
            }
          )
        }
      },
    }),

    // The detail page's "Recent activities" panel: this rating's customer's
    // WHOLE feedback history — both thumbs, conversation-backed or not — with
    // the viewed rating excluded server-side. See
    // OrderRatingRecentActivitiesParams for why this is a separate route rather
    // than getOrderRatings with a user_id filter.
    getOrderRatingRecentActivities: builder.query<
      PaginatedResponse<OrderRating>,
      OrderRatingRecentActivitiesParams
    >({
      query: ({ id, ...params }) => ({
        url: `order_ratings/${id}/recent_activities`,
        params,
      }),
      transformResponse: (
        response: PaginatedResponseWire<OrderRatingResponse>
      ): PaginatedResponse<OrderRating> => ({
        meta: normalizePaginationMeta(response.meta),
        data: response.data.map(transformOrderRating),
      }),
      // Both tags, for the same reason as getOrderRatingNeighbors: the panel
      // renders OTHER ratings, so resolving any one of them changes a row here
      // (its Resolved-by cell and Resolution). The scoped tag keeps this
      // rating's own resolve refetching it; the flat tag catches a sibling's.
      providesTags: (_result, _error, { id }) => [
        { type: 'OrderRating', id },
        'OrderRating',
      ],
      // The panel's ONLY telemetry, mirroring getOrderRatingNeighbors. A
      // failure renders an empty panel, which is indistinguishable from a
      // customer whose only rating is the one being viewed — and api/index's
      // baseQuery special-cases just 403 and the legal-docs redirect, so
      // nothing else reports it. Purely a side effect: the rejection is neither
      // swallowed nor re-thrown, so the hook's own isError is untouched.
      onQueryStarted: async ({ id }, { queryFulfilled }) => {
        try {
          await queryFulfilled
        } catch (rejection) {
          errorHandler.capture(
            'order_ratings/:id/recent_activities failed; the order-feedback customer history panel will render empty (indistinguishable from a customer with no other ratings)',
            {
              endpoint: 'getOrderRatingRecentActivities',
              // Only the rating id — the customer is derived server-side, so no
              // customer identifier rides on this call.
              id,
              rejection,
              likely_cause:
                'nexus order_ratings/:id/recent_activities not yet deployed',
            }
          )
        }
      },
    }),

    // The grant modal's reward catalog. Deliberately NOT the RAI slice's
    // `recovery/available_rewards`: that route is gated behind the `recovery-ai`
    // flag and 404s wherever it is dark, which is exactly how the dropdown ended
    // up empty in production (AI-1017). This route is backed by the same
    // interactor the customer-profile grant dropdown uses and emits identical
    // template hashids, so the modal, its props and AvailableReward are unchanged.
    getGrantableRewards: builder.query<AvailableReward[], void>({
      // Unpaginated (mirrors campaigns/grantable, the customer-profile dropdown's
      // source), so no fetch-until-drained loop — the catalog is bounded by
      // templates that have an active granted program, not by every published
      // template.
      query: () => ({ url: 'order_ratings/available_rewards' }),
      transformResponse: (response: { available_rewards: AvailableReward[] }) =>
        response.available_rewards,
      // The catalog's ONLY telemetry. api/index's baseQuery special-cases just
      // 403 and the legal-docs redirect, so a 404/500/network failure here is
      // otherwise entirely silent: no toast, no Sentry event, and a modal that
      // renders the same "no rewards" empty state it renders for a merchant with
      // a genuinely empty catalog. That indistinguishability IS the AI-1017
      // bug. The intended order is BE-first — the nexus route ships before this
      // — but nothing across the two repos enforces it, so this also defends
      // against the FE landing first: that window would 404 on every open, and
      // this makes it observable rather than something discovered months later
      // from a support ticket.
      //
      // Purely a side effect: the rejection is neither swallowed nor re-thrown,
      // so the hook's own `isError` (which drives the field's error copy) is
      // untouched. Mirrors resolveOrderRating's try/await/catch below.
      onQueryStarted: async (_arg, { queryFulfilled }) => {
        try {
          await queryFulfilled
        } catch (rejection) {
          errorHandler.capture(
            'order_ratings/available_rewards failed; the order-feedback grant catalog will render as empty',
            {
              endpoint: 'getGrantableRewards',
              // No customer data rides on this endpoint — the rejection carries
              // the reward catalog's own error envelope and nothing else.
              rejection,
              ticket: 'AI-1017',
              // Triage hint, not a prediction: BE-first is the intended order,
              // so a burst of these most likely means the FE reached an
              // environment ahead of the nexus route rather than a merchant
              // with nothing to grant.
              likely_cause:
                'nexus order_ratings/available_rewards not yet deployed',
            }
          )
        }
      },
    }),

    // Operator resolve for a rating with no RecoveryAI conversation. The wire
    // contract (URL, body, and how the backend is expected to persist it) is
    // documented on ResolveOrderRatingParams — this endpoint is FE-first and the
    // nexus route lands separately.
    //
    // `void` result, mirroring recoveryAi's restrictCustomer: the response body
    // is unused and the fresh state comes from the tag-driven refetch below —
    // the rating's own detail entry plus the whole index (a resolved row's
    // Resolved-by cell changes, and it drops out under "Hide resolved").
    resolveOrderRating: builder.mutation<void, ResolveOrderRatingParams>({
      query: ({ id, ...body }) => ({
        url: `order_ratings/${id}/resolve`,
        method: 'POST',
        body,
      }),
      // Success path only — a genuine failure must NOT drop the index/list
      // tags (a still-unresolved row must not vanish from "Hide resolved").
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'OrderRating', id },
        'OrderRating',
      ],
      // On FAILURE (422 already-resolved, conversation-backed, etc.),
      // invalidate just this rating's detail tag so it refetches. The refetch
      // reconciles the page on its own — an already-resolved rating comes back
      // with `resolved_by.kind` set and hides the action row, a
      // conversation-backed one comes back with a `conversationId` and the
      // page's existing redirect sends the operator to the real case detail.
      // The open dialog renders `isError` from this same rejection. For an
      // already-resolved rating the refetch unmounts that dialog (the action
      // row hides), so the page itself, now showing the rating as resolved, is
      // the operator's signal; no separate banner is kept for that case.
      onQueryStarted: async ({ id }, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled
        } catch {
          dispatch(
            orderRatingsApi.util.invalidateTags([{ type: 'OrderRating', id }])
          )
        }
      },
    }),

    // Send-only: emails the customer a subject + message WITHOUT resolving the
    // rating (merchant-api POST order_ratings/:id/messages). `void` — the
    // response body is unused. Scoped to this rating's own detail tag only, NOT
    // the flat `OrderRating` list tag: unlike resolveOrderRating, nothing about
    // resolved-status/list filtering changes on a send.
    sendOrderRatingMessage: builder.mutation<
      void,
      SendOrderRatingMessageParams
    >({
      query: ({ id, ...body }) => ({
        url: `order_ratings/${id}/messages`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'OrderRating', id },
      ],
    }),
  }),
})

export const {
  useGetGrantableRewardsQuery,
  useGetOrderRatingQuery,
  useGetOrderRatingNeighborsQuery,
  useGetOrderRatingRecentActivitiesQuery,
  useGetOrderRatingsQuery,
  useGetUserOrderRatingsQuery,
  useResolveOrderRatingMutation,
  useSendOrderRatingMessageMutation,
} = orderRatingsApi
